import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from './AuthContext';

// Create the UserContext
const UserContext = createContext(null);

/**
 * UserProvider component to manage user-related data and functionality
 * @param {Object} props - Component props
 * @returns {JSX.Element} Provider component
 */
export const UserProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [userCourses, setUserCourses] = useState([]);
  const [userCoursesIds, setUserCoursesIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize or get user data from Firestore
  const initializeUser = useCallback(async () => {
    if (!currentUser) {
      setUser(null);
      setUserCourses([]);
      setUserCoursesIds([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const userRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUser(userData);
        setUserCoursesIds(userData.registeredCourses || []);
      } else {
        // Create user document if it doesn't exist
        const newUser = {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || '',
          photoURL: currentUser.photoURL || '',
          registeredCourses: [],
          createdAt: new Date()
        };

        await setDoc(userRef, newUser);
        setUser(newUser);
        setUserCoursesIds([]);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error initializing user:", err);
      setError("Failed to load user data. Please try again.");
      setLoading(false);
    }
  }, [currentUser]);

  // Update user courses when available courses change
  const updateUserCourses = useCallback((availableCourses) => {
    if (!availableCourses || !Array.isArray(availableCourses) || availableCourses.length === 0) {
      setUserCourses([]);
      return;
    }

    // Map registered course IDs to full course objects
    const fullUserCourses = userCoursesIds
      .map(courseId => {
        // Find the full course object that matches this ID
        const matchingCourse = availableCourses.find(
          course => `${course.courseCode}-${course.courseSection}` === courseId
        );
        return matchingCourse || null;
      })
      .filter(Boolean); // Remove any null values (courses not found)

    setUserCourses(fullUserCourses);
  }, [userCoursesIds]);

  // Register for a course
  const registerCourse = useCallback(async (course) => {
    if (!currentUser) {
      throw new Error("You must be logged in to register for courses");
    }

    // Create a unique course identifier
    const courseId = `${course.courseCode}-${course.courseSection}`;

    // Check if user already registered for this course
    if (userCoursesIds.includes(courseId)) {
      throw new Error("You are already registered for this course");
    }

    try {
      setLoading(true);

      // Update user document in Firestore
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
        registeredCourses: arrayUnion(courseId)
      });

      // Update local state
      setUserCoursesIds(prev => [...prev, courseId]);
      setUserCourses(prev => [...prev, course]);

      setLoading(false);

      return {
        success: true,
        courseId,
        message: `Successfully registered for ${course.courseCode}: ${course.title}`
      };
    } catch (err) {
      console.error("Registration error:", err);
      setLoading(false);
      throw new Error("Failed to register for the course. Please try again later.");
    }
  }, [currentUser, userCoursesIds]);

  // Drop a course
  const dropCourse = useCallback(async (course) => {
    if (!currentUser) {
      throw new Error("You must be logged in to drop courses");
    }

    // Create a unique course identifier
    const courseId = `${course.courseCode}-${course.courseSection}`;

    try {
      setLoading(true);

      // Update user document in Firestore
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
        registeredCourses: arrayRemove(courseId)
      });

      // Update local state
      setUserCoursesIds(prev => prev.filter(id => id !== courseId));
      setUserCourses(prev => prev.filter(c => `${c.courseCode}-${c.courseSection}` !== courseId));

      setLoading(false);

      return {
        success: true,
        courseId,
        message: `Successfully dropped ${course.courseCode}: ${course.title}`
      };
    } catch (err) {
      console.error("Drop course error:", err);
      setLoading(false);
      throw new Error("Failed to drop the course. Please try again later.");
    }
  }, [currentUser]);

  // Check if a course is registered
  const isCourseRegistered = useCallback((course) => {
    if (!course) return false;

    const courseId = `${course.courseCode}-${course.courseSection}`;
    return userCoursesIds.includes(courseId);
  }, [userCoursesIds]);

  // Update user profile
  const updateUserProfile = useCallback(async (profileData) => {
    if (!currentUser) {
      throw new Error("You must be logged in to update your profile");
    }

    try {
      setLoading(true);

      // Update user document in Firestore
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, profileData);

      // Update local state
      setUser(prev => ({
        ...prev,
        ...profileData
      }));

      setLoading(false);

      return {
        success: true,
        message: "Profile updated successfully"
      };
    } catch (err) {
      console.error("Profile update error:", err);
      setLoading(false);
      throw new Error("Failed to update profile. Please try again later.");
    }
  }, [currentUser]);

  // Initialize user data when auth state changes
  useEffect(() => {
    initializeUser();
  }, [initializeUser]);

  // Value to be provided to consuming components
  const value = {
    user,
    userCourses,
    loading,
    error,
    registerCourse,
    dropCourse,
    isCourseRegistered,
    updateUserCourses,
    updateUserProfile
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

/**
 * Hook to use the UserContext
 * @returns {Object} User context value
 */
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export default UserContext;